/**
 * BRUSH AND DRAG-TO-ZOOM — two gestures, one destination.
 *
 * Both write the GLOBAL date range, so narrowing on one chart refilters the
 * whole screen rather than just that panel. That is the point: a manager who
 * drags across a spike wants everything else to answer for the same window.
 *
 * ── WHY THE BRUSH IS LIFTED OUT OF RECHARTS ──
 *
 * `syncId` in recharts 2.x synchronises the TOOLTIP AND THE CURSOR and nothing
 * else — it does not synchronise a `<Brush>`. Leaving the brush to the library
 * would give one chart a private window that nothing else on the screen shares,
 * and the caption above it would then be describing a different period from the
 * chart underneath. So the gesture reports its bucket keys and the filter is the
 * single source of truth; the brush's own extent is derived back from it.
 *
 * ── RESET IS ONE PRESS, NOT A GUESS ──
 *
 * The preset the reader was on before they zoomed is held in a ref, so "Reset
 * zoom" restores it exactly rather than falling back to a default they never
 * chose.
 */
import { useCallback, useRef, useState } from 'react';
import { CUSTOM_RANGE, DashboardFilter, Granularity } from '../state/filter-model';
import { FilterAction } from '../state/filter-reducer';
import { isDayKey } from '../../../../../function-utils/building-time';
import { bucketDays } from './use-chart-drill';

export interface ZoomSelection {
  from: string;
  to: string;
}

export interface ZoomHandlers {
  /** The band painted while the pointer is down. Null when not dragging. */
  selection: ZoomSelection | null;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  onMouseDown: (state: any) => void;
  onMouseMove: (state: any) => void;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  onMouseUp: () => void;
  /** From the Brush, with the two bucket KEYS it settled on. */
  onBrush: (fromKey: string, toKey: string) => void;
  /** True when the current window came from a gesture rather than a preset. */
  zoomed: boolean;
  reset: () => void;
}

/** Two bucket keys -> the DAYS the span covers, both inclusive.
 *
 *  A bucket key is already a calendar day on the clock the answer was cut on,
 *  so the span stays in keys and the server resolves them on that same clock
 *  (R-T5) — nothing here builds an instant on this device's clock. The LAST
 *  bucket is included whole: a reader dragging across the 1st to the 3rd means
 *  all of the 3rd, and a last WEEK or MONTH means all of it. */
export const windowFor = (
  fromKey: string,
  toKey: string,
  granularity: Granularity
): ZoomSelection | null => {
  if (!isDayKey(fromKey) || !isDayKey(toKey)) return null;
  const [start, last] = fromKey <= toKey ? [fromKey, toKey] : [toKey, fromKey];
  const end = bucketDays(last, granularity);

  return end ? { from: start, to: end.to } : null;
};

/**
 * THE BUCKET KEY UNDER THE POINTER, not the label printed on the axis.
 *
 * Every panel that offers this gesture plots a formatted label ("Jun 23",
 * "Week of 4 Aug") while the filter is written in raw YYYY-MM-DD bucket keys.
 * Reading `activeLabel` therefore produced a string `windowFor` could never
 * parse, and the whole gesture dispatched nothing.
 *
 * recharts hands the hovered row's own datum back in `activePayload`, and that
 * datum carries the key it was built from, so the key is taken from there. The
 * label is kept as the fallback for any chart whose rows have no key - such a
 * chart behaves exactly as it did before rather than throwing.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const bucketKeyFrom = (state: any): string | null => {
  const datum = state?.activePayload?.[0]?.payload;
  if (datum && typeof datum.key === 'string' && datum.key) return datum.key;
  const label = state?.activeLabel;

  return label == null ? null : String(label);
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface UseChartZoomOptions {
  filter: DashboardFilter;
  dispatch: (action: FilterAction, opts?: { replace?: boolean }) => void;
}

export const useChartZoom = ({
  filter,
  dispatch,
}: UseChartZoomOptions): ZoomHandlers => {
  /** What is DRAWN: axis labels, because the band is a ReferenceArea whose
   *  x1/x2 have to match the axis. */
  const [selection, setSelection] = useState<ZoomSelection | null>(null);
  const anchor = useRef<string | null>(null);
  /** What is DISPATCHED: raw bucket keys, which is what the filter speaks. */
  const anchorKey = useRef<string | null>(null);
  const latestKey = useRef<string | null>(null);
  /** The preset that was on before the first zoom of this session. */
  const previousRange = useRef<string | null>(null);

  const apply = useCallback(
    (fromKey: string, toKey: string) => {
      const w = windowFor(fromKey, toKey, filter.granularity);
      if (!w) return;
      if (filter.range !== CUSTOM_RANGE) previousRange.current = filter.range;
      dispatch({ type: 'setCustomRange', from: w.from, to: w.to });
    },
    [dispatch, filter.granularity, filter.range]
  );

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const onMouseDown = useCallback((state: any) => {
    const label = state?.activeLabel;
    if (label == null) return;
    anchor.current = String(label);
    anchorKey.current = bucketKeyFrom(state);
    latestKey.current = anchorKey.current;
    setSelection({ from: String(label), to: String(label) });
  }, []);

  const onMouseMove = useCallback((state: any) => {
    if (!anchor.current) return;
    const label = state?.activeLabel;
    if (label == null) return;
    latestKey.current = bucketKeyFrom(state);
    setSelection({ from: anchor.current, to: String(label) });
  }, []);
  /* eslint-enable @typescript-eslint/no-explicit-any */

  const onMouseUp = useCallback(() => {
    const current = selection;
    const fromKey = anchorKey.current;
    const toKey = latestKey.current;
    anchor.current = null;
    anchorKey.current = null;
    latestKey.current = null;
    setSelection(null);
    // A click is a drag of zero width, and a click belongs to the drill handler
    // rather than to the zoom. Requiring two different buckets is what keeps the
    // two gestures from fighting over the same press.
    if (!current || current.from === current.to) return;
    // The KEYS are what the filter is written in; `current` holds the axis
    // labels that were drawn. Passing the labels here is what made this gesture
    // silently do nothing for its whole life.
    if (!fromKey || !toKey) return;
    apply(fromKey, toKey);
  }, [apply, selection]);

  const onBrush = useCallback(
    (fromKey: string, toKey: string) => apply(fromKey, toKey),
    [apply]
  );

  const reset = useCallback(() => {
    dispatch({ type: 'setRange', range: previousRange.current || 'last_30_days' });
    previousRange.current = null;
  }, [dispatch]);

  return {
    selection,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onBrush,
    zoomed: filter.range === CUSTOM_RANGE,
    reset,
  };
};

/* `brushExtent` USED TO LIVE HERE, AND IT IS DELETED RATHER THAN WIRED.
 *
 * It derived the brush's start/end index from the filter, so that "the control
 * and the query cannot disagree" — a good idea that was written, exported and
 * then called by nothing, through every session of this plan. A dead export that
 * describes the right behaviour is worse than none: it reads like a decision
 * somebody made, and the next person wires it.
 *
 * It cannot be wired, and WP-35 established why while fixing the brush's ARIA.
 * `recharts` 2.15.4's `Brush` is UNCONTROLLED after mount: its
 * `getDerivedStateFromProps` re-syncs only when `data` identity or `updateId`
 * changes, so passing `startIndex`/`endIndex` from outside does not move it. The
 * extent would have been computed, handed over and ignored — a control that
 * does not move the thing it claims to control, which is worse than one that
 * merely looks stale.
 *
 * What actually keeps the two in agreement is the flow that exists: a gesture
 * reports its bucket KEYS, the filter is the single source of truth, and the
 * refetch that follows rebuilds the brush from the new data. See the header of
 * this file, and `charts/brush-aria.ts` for the recharts behaviour in detail.
 */
